import _ from 'lodash';
import { Amount, PaymentStatusType } from '../../api';
import { parseAmount } from '../../services';
import { getPaymentIndex } from '../services';

export interface ClaimPaymentSummaryItem {
    id?: string;
    status?: PaymentStatusType | string;
    created?: string;
    payerControlNumber?: string;
    allowedAmount?: Amount;
    payerAmount?: Amount;
    chargeAmount?: Amount;
    patientAmount?: Amount;
}

const getPaymentCreatedTimestamp = (created?: string): number => {
    if (!created) {
        return Number.NEGATIVE_INFINITY;
    }

    const timestamp = new Date(created).getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

/**
 * Among payments at the highest COB tier (tertiary > secondary > primary),
 * return only those with the latest `created` timestamp.
 */
export function getLatestPaymentsByPayerPriority<T extends ClaimPaymentSummaryItem>(payments: T[]): T[] {
    const primaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 0);
    const secondaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 1);
    const tertiaries = _.filter(payments, payment => getPaymentIndex(payment.status as PaymentStatusType) === 2);

    const priorityPayments = tertiaries.length ? tertiaries : secondaries.length ? secondaries : primaries;
    if (!priorityPayments.length) {
        return [];
    }

    const latestTimestamp = Math.max(...priorityPayments.map(payment => getPaymentCreatedTimestamp(payment.created)));
    return priorityPayments.filter(payment => getPaymentCreatedTimestamp(payment.created) === latestTimestamp);
}

const getReasonCodeTier = (status?: string): number | undefined => {
    // A denial has no COB tier of its own; it adjudicated the claim as the first payer to respond.
    if (status === PaymentStatusType.DENIED) {
        return 0;
    }
    return getPaymentIndex(status as PaymentStatusType);
};

/**
 * The payments whose CARC/RARC codes describe a line's current adjudication.
 *
 * Same tier and recency rules as `getLatestPaymentsByPayerPriority`, except a DENIED payment counts.
 * A denial's reason codes are the operative ones; dropping them hides why the line was not paid.
 */
export function getLatestAdjudicationsForReasonCodes<T extends ClaimPaymentSummaryItem>(payments: T[]): T[] {
    const tiered = payments.flatMap(payment => {
        const tier = getReasonCodeTier(payment.status);
        return tier === undefined ? [] : [{ payment, tier }];
    });
    if (!tiered.length) {
        return [];
    }

    const highestTier = Math.max(...tiered.map(({ tier }) => tier));
    const atTier = tiered.filter(({ tier }) => tier === highestTier).map(({ payment }) => payment);
    const latestTimestamp = Math.max(...atTier.map(payment => getPaymentCreatedTimestamp(payment.created)));
    return atTier.filter(payment => getPaymentCreatedTimestamp(payment.created) === latestTimestamp);
}

/**
 * Patient responsibility from the latest payment at the highest COB tier
 * (same approach as Record of Services summary).
 */
export function getPatientResponsibilityAmount(payments: ClaimPaymentSummaryItem[] = []): number {
    return getLatestPaymentsByPayerPriority(payments).reduce(
        (sum, payment) => sum + parseAmount(payment.patientAmount),
        0,
    );
}

export function getPaymentsWithoutReversals<T extends ClaimPaymentSummaryItem>(payments: T[] = []): T[] {
    const getTimestamp = (created?: string): number => {
        if (!created) {
            return Number.POSITIVE_INFINITY;
        }

        const timestamp = new Date(created).getTime();
        return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
    };

    const sortedPayments = _(payments)
        .slice()
        .sort((a, b) => {
            const aTs = getTimestamp(a.created);
            const bTs = getTimestamp(b.created);
            if (aTs === bTs) {
                return 0;
            }

            return aTs - bTs;
        })
        .value();

    const paymentReversals = sortedPayments.filter(
        payment => payment.status === PaymentStatusType.REVERSALOFPREVIOUSPAYMENT,
    );
    const nonReversalPayments = sortedPayments.filter(
        payment => payment.status !== PaymentStatusType.REVERSALOFPREVIOUSPAYMENT,
    );

    // Build stable FIFO queues per absolute charge amount from non-reversal payments.
    // This prevents index drift when multiple reversals share the same charge amount.
    const nonReversalQueuesByChargeAmount = new Map<string, T[]>();
    nonReversalPayments.forEach(payment => {
        const paymentChargeAmount = payment.chargeAmount;
        if (!paymentChargeAmount) {
            return;
        }

        const chargeAmountKey = Math.abs(parseAmount(paymentChargeAmount)).toString();
        const queue = nonReversalQueuesByChargeAmount.get(chargeAmountKey) || [];
        queue.push(payment);
        nonReversalQueuesByChargeAmount.set(chargeAmountKey, queue);
    });

    const reversedPayments = new Set<T>();

    paymentReversals.forEach(reversal => {
        let reversedPayment: T | undefined;
        const reversalControlNumber = reversal.payerControlNumber;
        if (reversalControlNumber) {
            reversedPayment = nonReversalPayments.find(payment => {
                if (reversedPayments.has(payment)) {
                    return false;
                }

                return payment.payerControlNumber === reversalControlNumber;
            });
        }

        if (reversedPayment) {
            reversedPayments.add(reversedPayment);
            return;
        }

        const chargeAmount = reversal.chargeAmount;
        if (!chargeAmount) {
            return;
        }

        const chargeAmountKey = Math.abs(parseAmount(chargeAmount)).toString();
        const queue = nonReversalQueuesByChargeAmount.get(chargeAmountKey);
        while (queue?.length && reversedPayments.has(queue[0])) {
            queue.shift();
        }

        reversedPayment = queue?.shift();
        if (reversedPayment) {
            reversedPayments.add(reversedPayment);
        }
    });

    return nonReversalPayments.filter(payment => !reversedPayments.has(payment));
}
